import React, { useState, useRef, useEffect } from 'react';
import { X, Palette } from 'lucide-react';

interface ColorSearchFilterProps {
  selectedColor: string | null;
  onColorSelect: (color: string | null) => void;
  similarity: number;
  onSimilarityChange: (value: number) => void;
  defaultOpen?: boolean; // Whether to show color wheel by default
}

// Convert HSL to RGB
function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  s /= 100;
  l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    return l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
  };
  return [Math.round(f(0) * 255), Math.round(f(8) * 255), Math.round(f(4) * 255)];
}

// Convert RGB to Hex
function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('').toUpperCase();
}

// Convert Hex to HSL
function hexToHsl(hex: string): [number, number, number] {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return [0, 100, 50];
  
  let r = parseInt(result[1], 16) / 255;
  let g = parseInt(result[2], 16) / 255;
  let b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
}

const ColorSearchFilter: React.FC<ColorSearchFilterProps> = ({
  selectedColor,
  onColorSelect,
  similarity,
  onSimilarityChange,
  defaultOpen = false
}) => {
  const [showPicker, setShowPicker] = useState(defaultOpen);
  const [hue, setHue] = useState(330); // Pink default
  const [saturation, setSaturation] = useState(80);
  const [lightness, setLightness] = useState(50);
  const [isReady, setIsReady] = useState(false); // Trigger for initial canvas draw
  const wheelCanvasRef = useRef<HTMLCanvasElement>(null);
  const slCanvasRef = useRef<HTMLCanvasElement>(null);

  // Trigger canvas drawing after component mounts
  useEffect(() => {
    setIsReady(true);
  }, []);

  // Initialize from selected color
  useEffect(() => {
    if (selectedColor) {
      const [h, s, l] = hexToHsl(selectedColor);
      setHue(h);
      setSaturation(s);
      setLightness(l);
    }
  }, []);

  // Draw the hue wheel
  useEffect(() => {
    const canvas = wheelCanvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const size = canvas.width;
    const centerX = size / 2;
    const centerY = size / 2;
    const outerRadius = size / 2 - 5;
    const innerRadius = outerRadius - 25;

    ctx.clearRect(0, 0, size, size);

    // Draw hue wheel
    for (let angle = 0; angle < 360; angle++) {
      const startAngle = (angle - 1) * Math.PI / 180;
      const endAngle = (angle + 1) * Math.PI / 180;
      
      ctx.beginPath();
      ctx.moveTo(
        centerX + innerRadius * Math.cos(startAngle),
        centerY + innerRadius * Math.sin(startAngle)
      );
      ctx.arc(centerX, centerY, outerRadius, startAngle, endAngle);
      ctx.arc(centerX, centerY, innerRadius, endAngle, startAngle, true);
      ctx.closePath();
      
      const [r, g, b] = hslToRgb(angle, 100, 50);
      ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
      ctx.fill();
    }

    // Draw selection indicator on wheel
    const indicatorAngle = hue * Math.PI / 180;
    const indicatorRadius = (outerRadius + innerRadius) / 2;
    const indicatorX = centerX + indicatorRadius * Math.cos(indicatorAngle);
    const indicatorY = centerY + indicatorRadius * Math.sin(indicatorAngle);

    ctx.beginPath();
    ctx.arc(indicatorX, indicatorY, 8, 0, Math.PI * 2);
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 3;
    ctx.stroke();
    ctx.strokeStyle = 'black';
    ctx.lineWidth = 1;
    ctx.stroke();

  }, [hue, isReady, showPicker]);

  // Draw the saturation/lightness square
  useEffect(() => {
    const canvas = slCanvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const size = canvas.width;

    // Create gradient for saturation (left to right)
    for (let x = 0; x < size; x++) {
      for (let y = 0; y < size; y++) {
        const s = (x / size) * 100;
        const l = 100 - (y / size) * 100;
        const [r, g, b] = hslToRgb(hue, s, l);
        ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
        ctx.fillRect(x, y, 1, 1);
      }
    }

    // Draw selection indicator
    const indicatorX = (saturation / 100) * size;
    const indicatorY = ((100 - lightness) / 100) * size;

    ctx.beginPath();
    ctx.arc(indicatorX, indicatorY, 8, 0, Math.PI * 2);
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 3;
    ctx.stroke();
    ctx.strokeStyle = 'black';
    ctx.lineWidth = 1;
    ctx.stroke();

  }, [hue, saturation, lightness, isReady, showPicker]);

  // Handle wheel click
  const handleWheelClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = wheelCanvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left - canvas.width / 2;
    const y = e.clientY - rect.top - canvas.height / 2;
    
    // Check if click is in the ring
    const distance = Math.sqrt(x * x + y * y);
    const outerRadius = canvas.width / 2 - 5;
    const innerRadius = outerRadius - 25;
    
    if (distance >= innerRadius && distance <= outerRadius) {
      // Calculate angle - atan2 gives angle from positive x-axis
      // Convert to degrees and normalize to 0-360
      let angle = Math.atan2(y, x) * 180 / Math.PI;
      if (angle < 0) angle += 360;
      setHue(Math.round(angle));
      
      // Auto-apply color
      const [r, g, b] = hslToRgb(Math.round(angle), saturation, lightness);
      onColorSelect(rgbToHex(r, g, b));
    }
  };

  // Handle SL square click
  const handleSLClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = slCanvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = Math.max(0, Math.min(canvas.width, e.clientX - rect.left));
    const y = Math.max(0, Math.min(canvas.height, e.clientY - rect.top));
    
    const s = Math.round((x / canvas.width) * 100);
    const l = Math.round(100 - (y / canvas.height) * 100);
    
    setSaturation(s);
    setLightness(l);
    
    // Auto-apply color
    const [r, g, b] = hslToRgb(hue, s, l);
    onColorSelect(rgbToHex(r, g, b));
  };

  // Get current color
  const getCurrentHex = () => {
    const [r, g, b] = hslToRgb(hue, saturation, lightness);
    return rgbToHex(r, g, b);
  };

  const clearColor = () => {
    onColorSelect(null);
  };

  const applyColor = () => {
    onColorSelect(getCurrentHex());
  };

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-lg border border-purple-500/30">
          <Palette className="icon-sm text-purple-400" />
          <label className="text-sm font-bold text-glass font-body">
            Search by Color
          </label>
        </div>
        {selectedColor && (
          <button
            onClick={clearColor}
            className="text-xs text-glass-secondary hover:text-glass flex items-center gap-1 font-body"
          >
            <X className="icon-xs" /> Clear
          </button>
        )}
      </div>

      {/* Selected color display */}
      {selectedColor ? (
        <div className="mb-3">
          <div className="flex items-center gap-3 p-3 bg-slate-800/60 rounded-lg">
            <div 
              className="w-12 h-12 rounded-lg shadow-inner border-2 border-white"
              style={{ backgroundColor: selectedColor }}
            />
            <div className="flex-1">
              <p className="font-mono font-bold text-glass">{selectedColor.toUpperCase()}</p>
              <p className="text-xs text-glass-secondary">Click below to change</p>
            </div>
          </div>
          
          {/* Similarity slider */}
          <div className="mt-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-glass-secondary font-body">Similarity</span>
              <span className="text-xs font-bold text-glass font-body">{similarity}%</span>
            </div>
            <input
              type="range"
              min="50"
              max="100"
              value={similarity}
              onChange={(e) => onSimilarityChange(Number(e.target.value))}
              className="w-full h-2 bg-slate-600 rounded-lg appearance-none cursor-pointer accent-purple-500"
            />
            <div className="flex justify-between text-xs text-glass-secondary mt-1 font-body">
              <span>More results</span>
              <span>Exact match</span>
            </div>
          </div>
        </div>
      ) : (
        <p className="text-xs text-glass-secondary mb-2 font-body">
          Pick a color to find similar polishes
        </p>
      )}

      {/* Color picker toggle */}
      <button
        onClick={() => setShowPicker(!showPicker)}
        className={`w-full px-3 py-2 rounded-lg text-sm font-medium transition-default flex items-center justify-center gap-2 font-body ${
          showPicker
            ? 'bg-slate-700 text-glass'
            : 'bg-slate-800/40 text-glass-secondary hover:bg-slate-700/60'
        }`}
      >
        <Palette className="icon-sm" />
        {showPicker ? 'Hide Color Wheel' : 'Open Color Wheel'}
      </button>

      {/* Color wheel picker */}
      {showPicker && (
        <div className="mt-3 p-4 bg-slate-800/60 rounded-lg">
          <div className="flex flex-col items-center gap-4">
            {/* Color wheel and SL square side by side on larger screens */}
            <div className="flex flex-col sm:flex-row items-center gap-4">
              {/* Hue wheel */}
              <div className="relative">
                <canvas
                  ref={wheelCanvasRef}
                  width={160}
                  height={160}
                  onClick={handleWheelClick}
                  className="cursor-crosshair"
                />
                {/* Center preview */}
                <div 
                  className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full border-4 border-white shadow-lg"
                  style={{ backgroundColor: getCurrentHex() }}
                />
              </div>

              {/* Saturation/Lightness square */}
              <div>
                <canvas
                  ref={slCanvasRef}
                  width={120}
                  height={120}
                  onClick={handleSLClick}
                  className="cursor-crosshair rounded-lg border-2 border-white shadow-md"
                />
                <div className="flex justify-between text-xs text-glass-secondary mt-1 px-1">
                  <span>Gray</span>
                  <span>Vivid</span>
                </div>
              </div>
            </div>

            {/* Current color info */}
            <div className="flex items-center gap-3 w-full">
              <div 
                className="w-10 h-10 rounded-lg border-2 border-white shadow-md flex-shrink-0"
                style={{ backgroundColor: getCurrentHex() }}
              />
              <div className="flex-1">
                <p className="font-mono font-bold text-glass text-sm">{getCurrentHex()}</p>
                <p className="text-xs text-glass-secondary">H: {hue}° S: {saturation}% L: {lightness}%</p>
              </div>
              <button
                onClick={applyColor}
                className="px-4 py-2 bg-slate-700 text-white rounded-lg text-sm font-bold hover:bg-slate-800 transition-default"
              >
                Apply
              </button>
            </div>

            {/* Quick presets - common nail polish colors */}
            <div className="w-full">
              <p className="text-xs text-glass-secondary mb-2">Quick picks:</p>
              <div className="flex flex-wrap gap-1 justify-center">
                {[
                  '#FF0000', '#FF69B4', '#FF1493', '#DC143C', // Reds & Pinks
                  '#FF4500', '#FFA500', '#FFD700', // Oranges & Yellows
                  '#32CD32', '#008080', '#00CED1', // Greens & Teals
                  '#4169E1', '#000080', '#8A2BE2', // Blues & Purples
                  '#800080', '#DDA0DD', '#E6E6FA', // Purples & Lavenders
                  '#FFFFFF', '#808080', '#000000', // Neutrals
                  '#8B4513', '#D2691E', '#C0C0C0', // Browns & Metallics
                ].map((color, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      const [h, s, l] = hexToHsl(color);
                      setHue(h);
                      setSaturation(s);
                      setLightness(l);
                      onColorSelect(color);
                    }}
                    className="w-6 h-6 rounded border border-slate-600 hover:scale-110 transition-transform"
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ColorSearchFilter;

// Utility function to calculate color distance (for use in filtering)
export function colorDistance(hex1: string, hex2: string): number {
  // Convert hex to RGB
  const rgb1 = hexToRgb(hex1);
  const rgb2 = hexToRgb(hex2);
  
  if (!rgb1 || !rgb2) return 100;

  // Calculate Euclidean distance in RGB space
  // Weighted to account for human perception (green is more sensitive)
  const rDiff = rgb1.r - rgb2.r;
  const gDiff = rgb1.g - rgb2.g;
  const bDiff = rgb1.b - rgb2.b;
  
  // Weighted Euclidean distance
  const distance = Math.sqrt(
    2 * rDiff * rDiff + 
    4 * gDiff * gDiff + 
    3 * bDiff * bDiff
  );
  
  // Normalize to 0-100 scale (max possible distance is ~764)
  const normalizedDistance = (distance / 764) * 100;
  
  // Return similarity percentage (100 = identical, 0 = completely different)
  return Math.max(0, 100 - normalizedDistance);
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}
